import { ColumnDef } from '@tanstack/react-table'
import { FC } from 'react'
import Link from '~/components/link'
import { Avatar } from '~/features/avatar'
import { useUserById } from '~/features/datastore'
import { UserRecord } from '~/stores/stores.types'
import { User } from '~/utils/records'

import { Icon } from '@hap/components'
import { get } from '@hap/utils'

import { MakeColumnOptions } from './types'

export const UserCell: FC<{
  drilldown?: boolean
  user?: {
    id: string
    firstName: string
    lastName: string
    avatarFileId?: string | null
    email?: string
  }
  variant?: 'lg'
}> = ({ user, variant, drilldown }) => {
  if (!user) {
    return <Icon name="minus" />
  }

  const inner = (
    <span className="flex items-center gap-3">
      <Avatar
        firstName={user.firstName}
        lastName={user.lastName}
        avatarFileId={user.avatarFileId}
        size={variant === 'lg' ? 'md' : 'sm'}
      />
      <span className="grid gap-0 leading-5">
        <span className="truncate font-semibold">
          {user.firstName} {user.lastName}
        </span>
        {variant === 'lg' && <span className="text-muted-foreground">{user.email}</span>}
      </span>
    </span>
  )

  // If `drilldown` is true, render the inner content without wrapping it in a Link.
  // This provides a static display and avoids navigation. When `drilldown` is false,
  // wrap the content in a Link to enable navigation to the user's details page.
  return drilldown ? (
    inner
  ) : (
    <Link to={`/members/${user.id}`} drilldown className="truncate">
      {inner}
    </Link>
  )
}

interface Props {
  userId: ID
  fallbackLabel?: string
  variant?: 'lg'
}

export const RelayUserCell: FC<Props> = (props) => {
  const { record: user } = useUserById(props.userId)
  if (props.userId === 'null' || props.userId === '')
    return <span className="text-nowrap">{props.fallbackLabel}</span>

  if (!user) {
    console.error(`UserCell: user not found '${props.userId}'`)
  }

  return user ? (
    <UserCell user={user} />
  ) : (
    <span className="text-nowrap">{props.userId}</span>
  )
}

export function makeUserColumnTrpc<D extends UnknownRecord>({
  fieldKey,
  header = 'Agent',
  sortable = false,
  sticky,
  width = 220,
  minWidth = width,
  maxWidth,
  variant,
  footer,
}: MakeColumnOptions<D> & { variant?: 'lg' }): ColumnDef<D> {
  return {
    accessorKey: fieldKey,
    id: fieldKey as string,
    cell: (cell) => {
      const users = cell.getValue()
      return Array.isArray(users) ? (
        users.map((user) => {
          return (
            <UserCell
              variant={variant}
              user={
                typeof user === 'string'
                  ? (cell.row.original as User)
                  : (user as UserRecord)
              }
            />
          )
        })
      ) : (
        <UserCell
          variant={variant}
          user={
            typeof cell.getValue<ID>() === 'string'
              ? (cell.row.original as User)
              : cell.getValue<UserRecord>()
          }
        />
      )
    },
    header,
    size: width,
    minSize: minWidth,
    maxSize: maxWidth,
    meta: { sticky, minWidth: width },
    enableSorting: sortable,
    footer,
  }
}

export function makeUserColumnV2<D extends UnknownRecord>({
  fieldKey,
  header = 'Agent',
  sortable = false,
  sticky,
  width = 180,
  fallbackLabel,
}: MakeColumnOptions<D> & {
  fallbackLabel?: string
  userId?: ID
}): ColumnDef<D> {
  return {
    accessorKey: fieldKey,
    id: fieldKey as string,
    cell: (cell) => (
      <RelayUserCell
        userId={(get(cell.row.original, 'id') as ID) || cell.getValue<ID>()}
        fallbackLabel={fallbackLabel}
      />
    ),
    header,
    meta: { sticky, width },
    enableSorting: sortable,
  }
}
